import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, Pause } from "lucide-react";
import { scrollTo } from "@/lib/scroll";
import { EASE } from "@/components/Reveal";

function MaskedLine({ children, delay }) {
    return (
        <span className="block overflow-hidden pb-[0.12em]">
            <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero() {
    const ref = useRef(null);
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(true);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const videoScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    const toggle = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) {
            v.play();
            setPlaying(true);
        } else {
            v.pause();
            setPlaying(false);
        }
    };

    return (
        <section
            id="home"
            ref={ref}
            data-testid="hero-section"
            className="relative h-[100svh] min-h-[620px] overflow-hidden bg-[#1A1A1A]"
        >
            <motion.div style={{ y: videoY, scale: videoScale }} className="absolute inset-0">
                <video
                    ref={videoRef}
                    data-testid="hero-video"
                    className="h-full w-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                >
                    <source src="/assets/promo-video.mp4" type="video/mp4" />
                    <source src="/assets/promo-video.webm" type="video/webm" />
                </video>
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/45 via-[#1A1A1A]/30 to-[#1A1A1A]/75" />

            <motion.div
                style={{ y: contentY, opacity: contentOpacity }}
                className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-28 sm:px-8 sm:pb-32"
            >
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
                    className="mb-6 text-xs font-medium uppercase tracking-[0.35em] text-[#D4A5A5]"
                    data-testid="hero-overline"
                >
                    Consulenza · Formazione · Sviluppo
                </motion.p>

                <h1
                    data-testid="hero-title"
                    className="font-serif text-[13vw] leading-[1.02] tracking-tight text-[#FAF7F2] sm:text-7xl lg:text-8xl"
                >
                    <MaskedLine delay={0.4}>Ti portiamo</MaskedLine>
                    <MaskedLine delay={0.55}>verso il tuo</MaskedLine>
                    <MaskedLine delay={0.7}>
                        <span className="italic text-[#D4A5A5]">obiettivo</span>.
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
                    className="mt-7 max-w-xl text-base leading-relaxed text-[#FAF7F2]/75 sm:text-lg"
                    data-testid="hero-subtitle"
                >
                    GPS srls nasce dall'esperienza di professionisti con oltre 10 anni nel campo, uniti in un progetto
                    comune. Centrati sul valore, sull'ascolto e sul supporto alle persone e alle imprese.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
                    className="mt-9 flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="hero-primary-cta"
                        onClick={() => scrollTo("#contatti")}
                        className="group flex items-center gap-2 rounded-full bg-[#D4A5A5] px-7 py-3.5 text-sm font-medium tracking-wide text-[#1A1A1A] transition-colors duration-300 hover:bg-[#FAF7F2]"
                    >
                        Parliamo del tuo obiettivo
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                    <button
                        data-testid="hero-secondary-cta"
                        onClick={() => scrollTo("#metodo")}
                        className="rounded-full border border-[#FAF7F2]/30 px-7 py-3.5 text-sm font-medium tracking-wide text-[#FAF7F2] transition-colors duration-300 hover:border-[#D4A5A5] hover:text-[#D4A5A5]"
                    >
                        Scopri il metodo
                    </button>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.5 }}
                className="absolute bottom-8 right-5 z-10 flex items-center gap-4 sm:right-8"
            >
                <span className="hidden text-xs uppercase tracking-[0.25em] text-[#FAF7F2]/50 sm:block">
                    Il nostro modo di lavorare
                </span>
                <button
                    data-testid="video-toggle-play-button"
                    onClick={toggle}
                    aria-label={playing ? "Metti in pausa il video" : "Riproduci il video"}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-[#FAF7F2]/30 text-[#FAF7F2] backdrop-blur-sm transition-colors duration-300 hover:border-[#D4A5A5] hover:text-[#D4A5A5]"
                >
                    {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
                </button>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.7 }}
                className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
            >
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#FAF7F2]/50">Scorri</span>
                <div className="h-10 w-px overflow-hidden bg-[#FAF7F2]/15">
                    <div className="scroll-cue-line h-full w-full bg-[#D4A5A5]" />
                </div>
            </motion.div>
        </section>
    );
}
